import React, { useEffect, useState } from 'react';
import { Alert, DatePicker, Modal } from 'antd';

import TagBadge from './TagBadge';
import { RecordIdentity } from '../common/RecordBadge';
import useDateFormat from '../../hooks/useDateFormat';
import { activeTagRefusal, isPastDay, untilEndOfDay } from '../../utils/activeTag';
import { tagIdOf } from '../../utils/tags';

/**
 * MAKE ACTIVE… (docs/plans/tags-active-tag-proposal.md; build calls b50–b52):
 * the small dialog the tag page's ⋯ and a private tag's ⋯ in Account ›
 * Private tags open. The tag's identity, what an active tag does in one line,
 * an OPTIONAL end date ("Until", empty by default — it stays on until it is
 * turned off), and one primary, **Make active**. When another tag is on, it
 * says that this one takes its place (one at a time, b51).
 *
 * `onMakeActive(tagId, { until })` writes (useActiveTag's `makeActive`) and
 * throws a refusal back; the dialog shows it and stays open. A save closes
 * the dialog (docs/platform-standards.md § 8: a save finishes the modal).
 */
export default function MakeActiveTagModal({ open, tag, current = null, onMakeActive, onClose }) {
	const dateFormat = useDateFormat();
	const [until, setUntil] = useState(null);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);

	// A fresh dialog every time it opens: no end date, no old refusal.
	useEffect(() => {
		if (!open) return;
		setUntil(null);
		setBusy(false);
		setRefusal(null);
	}, [open]);

	const replacing = current && current.tag && tag && current.tagId !== tagIdOf(tag) ? current.tag : null;

	const submit = async () => {
		if (!tag || busy) return;
		setBusy(true);
		setRefusal(null);
		try {
			await onMakeActive(tagIdOf(tag), { until: untilEndOfDay(until) });
			onClose();
		} catch (error) {
			setRefusal(activeTagRefusal(error));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Make active"
			okText="Make active"
			onOk={submit}
			okButtonProps={{ loading: busy, disabled: !tag }}
			onCancel={onClose}
			destroyOnHidden
			className="active-tag-modal"
		>
			{tag ? (
				<div className="modal-identity">
					<RecordIdentity kicker="Private tag" badge={<TagBadge tag={tag} size="sm" />} />
				</div>
			) : null}
			{refusal ? <Alert type="error" showIcon className="active-tag-modal__refusal" message={refusal} data-testid="active-tag-refusal" /> : null}
			<p className="active-tag-modal__lead">
				Everything you add from here on gets this tag, until you stop it. You see it on each new item before you
				save, and can leave it off that one. Nothing you already have changes.
			</p>
			{replacing ? (
				<Alert
					type="info"
					showIcon
					className="active-tag-modal__replacing"
					data-testid="active-tag-replacing"
					message={(
						<span className="active-tag-modal__replacing-text">
							You’re adding <TagBadge tag={replacing} size="xs" /> now. This one takes its place: one tag is active at a time.
						</span>
					)}
				/>
			) : null}
			<label className="active-tag-modal__field">
				<span className="micro-label">Until (optional)</span>
				<DatePicker
					value={until}
					onChange={setUntil}
					format={dateFormat}
					disabledDate={isPastDay}
					allowClear
					placeholder="No end date"
					aria-label="Until (optional)"
					className="active-tag-modal__until"
					inputReadOnly
				/>
				<span className="meta-line">Leave it empty to keep it on until you stop it.</span>
			</label>
		</Modal>
	);
}
